import React, { FC, useEffect } from 'react';

import { Fit, Layout, useRive } from '@rive-app/react-canvas';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { DETECTION_STAGE } from '~/modules/tools/views/PlagiarismCheckerV4/constants';
import { getDetectionStage } from '~/modules/tools/views/PlagiarismCheckerV4/helpers';
import { onAssetLoad } from '~/utils/animations';

import styles from './styles.module.scss';
import AnimationSrc from '../../assets/plagiarism_report.riv';

enum PLAGIARISM_REPORT_ANIMATION {
  RED = 'RedPlag',
  YELLOW = 'YellowPlag',
  GREEN = 'GreenPlag',
}

const getTexts = (t: TFunction): { runName: string; text: string }[] => {
  return [
    {
      runName: 'SourcesIncluded',
      text: t(
        'modal:feature_unlock.plagiarism_report_animation_text[0]',
        'Sources included',
      ),
    },
    {
      runName: 'plagiarism',
      text: t(
        'modal:feature_unlock.plagiarism_report_animation_text[1]',
        'Plagiarism',
      ),
    },
  ];
};

const getAnimation = (stage: DETECTION_STAGE): PLAGIARISM_REPORT_ANIMATION => {
  if (stage === DETECTION_STAGE.LOW_LEVEL) {
    return PLAGIARISM_REPORT_ANIMATION.GREEN;
  }

  if (stage === DETECTION_STAGE.HIGH_LEVEL) {
    return PLAGIARISM_REPORT_ANIMATION.RED;
  }

  return PLAGIARISM_REPORT_ANIMATION.YELLOW;
};

type Props = {
  plagiarismResult: number;
};

const PlagiarismReportAnimation: FC<Props> = ({ plagiarismResult }) => {
  const { t } = useTranslation();

  const DetectionStage = getDetectionStage(plagiarismResult);

  const { rive, RiveComponent } = useRive({
    src: AnimationSrc,
    autoplay: true,
    animations: getAnimation(DetectionStage),
    layout: new Layout({
      fit: Fit.Cover,
    }),
    assetLoader: onAssetLoad,
  });

  useEffect(() => {
    if (rive) {
      [
        { runName: 'percentage', text: `${plagiarismResult}%` },
        ...getTexts(t),
      ].forEach(({ runName, text }) => {
        rive.setTextRunValue(runName, text);
      });
    }
  }, [rive]);

  return (
    <div className={styles.animation}>
      <RiveComponent />
    </div>
  );
};

export default PlagiarismReportAnimation;
